import React from 'react';
import styled from 'styled-components';

import { useTranslation } from '@lt/localization';
import FlightDuration from './FlightDuration/FlightDuration';
import { formatDateToDay } from '../../../../../../utils/dateUtils';
import { getDateWithoutTimezone, getHoursSafe } from '../../utils/flightUtils';
import EmptyTimeTooltipContainer from '../../containers/Flights/EmptyTimeTooltip';
import FlightsLuggage from './Luggage/FlightsLuggage/FlightsLuggage';
import type { FlightsData } from '../../types/flights';

const baseCls = 'checkout-flight-route';

interface FlightsRouteProps {
  data: FlightsData['to'];
  type: 'to' | 'back';
  isAdditional?: boolean;
  isMobile?: boolean;
  showLogo?: boolean;
}

const FlightsRoute = ({
  data,
  type,
  showLogo,
  isAdditional,
  isMobile,
}: FlightsRouteProps) => {
  const { t } = useTranslation();
  const {
    arrival,
    departure,
    origin,
    destination,
    time,
    airline: { name, logo },
    transition,
    transition_time,
    direct,
    luggage,
  } = data;

  const parseDeparture = getDateWithoutTimezone(departure);
  const parseArrival = getDateWithoutTimezone(arrival);

  const durationTooltipProps = !direct
    ? {
        'data-route-info': JSON.stringify({
          transition,
          transition_time,
        }),
      }
    : {};

  return (
    <StyledMainContainer $isDirectionTo={type === 'to'}>
      <StyleFlightInfo>
        <FlightsLuggage
          className={`${baseCls}__luggage`}
          luggage={luggage}
          isMobile={isMobile}
        />
      </StyleFlightInfo>

      <StyledWrapper>
        <StyledContainer $isMobile={isMobile}>
          <StyledRow>
            <StyledDeparture $isMobile={isMobile} $noTime={!time}>
              <StyledTime
                $isAdditional={isAdditional}
                $isDirectionTo={type === 'to'}
              >
                {!time ? (
                  <>
                    {t('monthSegments.noData')}
                    <StyledEmptyTimeTooltipContainer
                      $noTime={!time}
                      type={type}
                      departure={parseDeparture}
                    />
                  </>
                ) : (
                  getHoursSafe(parseDeparture)
                )}
              </StyledTime>
              {departure && (
                <StyledDate $isAdditional={isAdditional}>
                  {formatDateToDay(parseDeparture)}
                </StyledDate>
              )}
              <StyledAirport
                $isMobile={isMobile}
                title={origin.name}
                $isAdditional={isAdditional}
              >
                {origin.name}
              </StyledAirport>
            </StyledDeparture>
            {time && (
              <StyledFlightsDurationContainer
                $isDirectionTo={type === 'to'}
                $isMobile={isMobile}
                {...durationTooltipProps}
              >
                <FlightDuration
                  value={time}
                  direct={direct}
                  isAdditional={isAdditional}
                  isMobile={!!isMobile}
                />
              </StyledFlightsDurationContainer>
            )}
          </StyledRow>
        </StyledContainer>
        <StyledArrival $isMobile={isMobile}>
          <StyledTimeMedium
            $isAdditional={isAdditional}
            $needMargin={!isMobile}
          >
            {!time ? t('monthSegments.noData') : getHoursSafe(parseArrival)}
          </StyledTimeMedium>
          {arrival && (
            <StyledDate $isAdditional={isAdditional}>
              {formatDateToDay(parseArrival)}
            </StyledDate>
          )}
          <StyledAirport
            $isMobile={isMobile}
            title={destination.name}
            $isAdditional={isAdditional}
          >
            {destination.name}
          </StyledAirport>
        </StyledArrival>

        {showLogo && logo && (
          <StyledLogo>
            <StyledImg src={logo.src} alt={name} title={name} />
          </StyledLogo>
        )}
      </StyledWrapper>
    </StyledMainContainer>
  );
};

const StyleFlightInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const StyledMainContainer = styled.div<{ $isDirectionTo: boolean }>`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  color: #4d4d4d;
  ${({ $isDirectionTo }) => $isDirectionTo && `margin-top: 18px;`}
`;

const StyledEmptyTimeTooltipContainer = styled(EmptyTimeTooltipContainer)<{
  $noTime: boolean;
}>`
  ${({ $noTime }) =>
    $noTime &&
    `
    margin-left: 5px;
    vertical-align: middle;
  `}
  margin-left: 5px;
  vertical-align: middle;
`;
const StyledDeparture = styled.div<{ $isMobile?: boolean; $noTime: boolean }>`
  ${({ $isMobile }) =>
    $isMobile &&
    `
      overflow: visible;
      width: auto;
      font-size: 13px;
  `}

  ${({ $noTime }) =>
    $noTime &&
    `
      margin-right: 50px;
      width: auto;
      min-width: 178px;
  `}

  min-width: 75px;
  font-size: 14px;
  width: 80px;
`;

const StyledArrival = styled.div<{ $isMobile?: boolean }>`
  position: relative;
  flex-grow: 1;
  min-width: 75px;
  font-size: 14px;
  ${({ $isMobile }) =>
    $isMobile &&
    `
      position: relative;
      width: auto;
      min-width: 0;
      overflow: visible;
      width: auto;
      font-size: 13px;
  `}
`;

const StyledTimeMedium = styled.div<{
  $needMargin: boolean;
  $isAdditional?: boolean;
  $isMobile?: boolean;
}>`
  ${({ $needMargin }) => $needMargin && `margin-top: 10px;`}
  ${({ $isAdditional }) => $isAdditional && `color: white;`}
  font-size: 18px;
  font-weight: bold;
  ${({ $isMobile }) => $isMobile && 'font-size: 15px;'}
`;

const StyledWrapper = styled.div`
  padding-top: 0;
  display: flex;
  min-width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledContainer = styled.div<{ $isMobile?: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
      min-width: auto;
      max-width: auto;
      width: auto;`
      : `
      min-width: 235px;
      max-width: 315px;
      width: 100%;
  `}
`;

const StyledTime = styled.div<{
  $isAdditional?: boolean;
  $isDirectionTo: boolean;
  $isMobile?: boolean;
}>`
  margin-top: 0;
  color: #17181a;
  font-size: 28px;
  ${({ $isAdditional }) => $isAdditional && `color: white;`}
  ${({ $isDirectionTo }) => $isDirectionTo && `font-weight: bold;`}
  ${({ $isMobile }) => $isMobile && 'font-size: 18px;'}
`;

const StyledDate = styled.div<{ $isAdditional?: boolean }>`
  white-space: nowrap;
  ${({ $isAdditional }) => $isAdditional && `color: white;`}
`;

const StyledAirport = styled.div<{
  $isMobile?: boolean;
  $isAdditional?: boolean;
}>`
  width: ${({ $isMobile }) => ($isMobile ? `100%` : `auto`)};
  ${({ $isAdditional }) => $isAdditional && `color: white;`}
`;

const StyledLogo = styled.div`
  margin-left: 10px;
  display: flex;
  max-height: 70px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: 100px;
`;

const StyledImg = styled.img`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const StyledFlightsDurationContainer = styled.div<{
  $isDirectionTo: boolean;
  $isMobile?: boolean;
}>`
  ${({ $isMobile }) =>
    $isMobile &&
    `
      margin-top: -11px;
      margin-right: 10px;
      margin-left: -10px;
      padding: 0;
      width: auto;
      min-width: 100px;
  `}
  margin-right: 10px;
  margin-left: 5px;
  width: 100%;
  min-width: 140px;
  max-width: 220px;
  flex-shrink: 10;
  text-align: center;

  ${({ $isDirectionTo }) => $isDirectionTo && `padding-top: 5px;`}
`;

export default FlightsRoute;
